import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { ModalBody } from 'reactstrap';
import { Cropper } from 'react-cropper';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'cropperjs/dist/cropper.css';

import { ReactComponent as UploaderIcon } from 'assets/icon-up-file.svg';
import { ReactComponent as AdjustSizeIcon } from 'assets/icon-simulator-area.svg';
import { ReactComponent as ZoomInIcon } from 'assets/icon-zoom-in.svg';
import { ReactComponent as ZoomOutIcon } from 'assets/icon-zoom-out.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/icon-arrow-down.svg';
import { ReactComponent as ArrowUpIcon } from 'assets/icon-arrow-up.svg';
import { ReactComponent as ArrowLeftIcon } from 'assets/icon-arrow-left.svg';
import { ReactComponent as ArrowRightIcon } from 'assets/icon-arrow-right.svg';
import { ReactComponent as RotateIcon } from 'assets/icon-rotate.svg';
import { ReactComponent as FlipVerticalIcon } from 'assets/icon-flip-vertical.svg';
import { ReactComponent as FlipHorizontalIcon } from 'assets/icon-flip-horizontal.svg';
import { colors, device } from 'shared/defaultTheme';
import { LabelInput, LabelRequiredInput } from './Labels';
import { Modal } from './modal/Modals';
import { ModalHeader } from './modal/ModalHeaders';
import { Button, ButtonIcon } from './Buttons';

const UploadedImage = styled.div(({ imageSrc }) => ({
  background: `url('${imageSrc}')`,
  backgroundPosition: 'center',
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
}));

const MaximumSizeLabel = styled.span`
  color: ${({ color }) => color};
  font-size: 16px;
  letter-spacing: 0;
  line-height: 19px;
`;

const ContainerTools = styled.div`
  background-color: ${colors.ghostWhite};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  width: auto;
  @media ${device.sm} {
    padding: 3px;
  }
`;

const ModalImageCropper = ({ isOpen, toggle, imageSrc, mainColor, onSaveData }) => {
  const { t } = useTranslation();
  const [cropper, setCropper] = useState();
  const [zoom, setZoom] = useState(1);
  const [moveX, setMoveX] = useState(1);
  const [moveY, setMoveY] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [scaleY, setScaleY] = useState(0);
  const [scaleX, setScaleX] = useState(0);

  useEffect(() => {
    setZoom(0);
    setMoveX(1);
    setMoveY(1);
    setRotate(0);
    setScaleY(0);
    setScaleX(0);
  }, [isOpen]);

  const saveCropperData = () => {
    if (cropper !== null && cropper !== undefined) {
      onSaveData(cropper?.getCroppedCanvas()?.toDataURL());
      toggle();
    }
  };

  return (
    <Modal className="modal-cropper" isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader
        icon={<AdjustSizeIcon fill={colors.white} />}
        label={t('general_adjust_size_image_title')}
        backgroundColor={mainColor}
        onClose={toggle}
      />
      <ModalBody className="modal-body-cropper">
        <div className="cropper-wrapper">
          <Cropper
            style={{ height: 323, width: '100%' }}
            zoomTo={zoom}
            initialAspectRatio={1}
            preview=".cropper-img-preview"
            src={imageSrc}
            viewMode={1}
            guides
            background
            responsive
            autoCropArea={1}
            checkOrientation={false}
            onInitialized={(instance) => setCropper(instance)}
          />
        </div>
        <div className="cropper-tools-section">
          <MaximumSizeLabel className="headline" color={mainColor}>
            {t('general_max_image_size')}
          </MaximumSizeLabel>
          <div className="cropper-tools-section__tools-wrapper">
            <ContainerTools>
              <ButtonIcon
                type="button"
                Icon={() => <ZoomInIcon fill={colors.ecru} />}
                className="button-icon"
                onClick={() => {
                  const currentZoom = zoom + 1.8;
                  setZoom(currentZoom);
                  cropper.zoomTo(currentZoom);
                }}
              />
              <ButtonIcon
                type="button"
                Icon={() => <ZoomOutIcon fill={colors.ecru} />}
                className="button-icon"
                onClick={() => {
                  const currentZoom = zoom - 0.5;
                  setZoom(currentZoom);
                  cropper.zoomTo(currentZoom);
                }}
              />
            </ContainerTools>
            <ContainerTools>
              <ButtonIcon
                type="button"
                Icon={() => <ArrowDownIcon fill={colors.ecru} />}
                className="button-icon"
                onClick={() => {
                  const currentMoveY = moveY - 2 * zoom;
                  setMoveY(currentMoveY);
                  cropper.moveTo(moveX, currentMoveY);
                }}
              />
              <ButtonIcon
                type="button"
                Icon={() => <ArrowUpIcon fill={colors.ecru} />}
                className="button-icon"
                onClick={() => {
                  const currentMoveY = moveY + 1 * zoom;
                  setMoveY(currentMoveY);
                  cropper.moveTo(moveX, currentMoveY);
                }}
              />
              <ButtonIcon
                type="button"
                Icon={() => <ArrowLeftIcon fill={colors.ecru} />}
                className="button-icon"
                onClick={() => {
                  const currentX = moveX + 2 * zoom;
                  setMoveX(currentX);
                  cropper.moveTo(currentX, moveY);
                }}
              />
              <ButtonIcon
                type="button"
                Icon={() => <ArrowRightIcon fill={colors.ecru} />}
                className="button-icon"
                onClick={() => {
                  const currentX = moveX - 2 * zoom;
                  setMoveX(currentX);
                  cropper.moveTo(currentX, moveY);
                }}
              />
            </ContainerTools>
            <ContainerTools>
              <ButtonIcon
                type="button"
                Icon={() => (
                  <RotateIcon
                    fill={colors.ecru}
                    style={{ transform: 'rotate(-20deg) scaleX(-1)' }}
                  />
                )}
                className="button-icon"
                onClick={() => {
                  const currentRotate = rotate === -360 ? 0 : rotate - 10;
                  setRotate(currentRotate);
                  cropper.rotateTo(currentRotate);
                }}
              />
              <ButtonIcon
                type="button"
                Icon={() => <RotateIcon fill={colors.ecru} />}
                className="button-icon"
                onClick={() => {
                  const currentRotate = rotate === 360 ? 0 : rotate + 10;
                  setRotate(currentRotate);
                  cropper.rotateTo(currentRotate);
                }}
              />
            </ContainerTools>
            <ContainerTools>
              <ButtonIcon
                type="button"
                Icon={() => <FlipVerticalIcon fill={colors.ecru} />}
                className="button-icon"
                onClick={() => {
                  const currentScaleY = scaleY - 1;
                  setScaleY(currentScaleY);
                  cropper.scaleY(currentScaleY);
                }}
              />
              <ButtonIcon
                type="button"
                Icon={() => <FlipHorizontalIcon fill={colors.ecru} />}
                className="button-icon"
                onClick={() => {
                  const currentScaleX = scaleX - 1;
                  setScaleX(currentScaleX);
                  cropper.scaleX(currentScaleX);
                }}
              />
            </ContainerTools>
          </div>
        </div>
        <div className="cropper-preview-section">
          <span className="cropper-preview-section__text">{t('general_preview')}</span>
          <div className="cropper-img-preview" />
        </div>
        <div className="modal-footer-cropper">
          <Button
            className="footer-button footer-button--space"
            label={t('general_discard')}
            color="secondary"
            type="button"
            onClick={toggle}
            textColor={mainColor}
          />
          <Button
            className="footer-button cropper-footer-button"
            label={t('general_button_save')}
            color="primary"
            type="button"
            onClick={saveCropperData}
            backgroundColor={mainColor}
          />
        </div>
      </ModalBody>
    </Modal>
  );
};

ModalImageCropper.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  imageSrc: PropTypes.string.isRequired,
  mainColor: PropTypes.string.isRequired,
  onSaveData: PropTypes.func.isRequired,
};

export const ImageUploader = ({
  className,
  mainColor,
  label,
  labelClassName,
  labelColor,
  required,
  imageBase64,
  onChangeFile,
  onSaveBase64,
  onError,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef();
  const [openModalCropper, setOpenModalCropper] = useState(false);

  const toggleModalCropper = () => setOpenModalCropper(!openModalCropper);

  const validateTypeFile = (file) => {
    const validTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/svg+xml',
      'image/x-icon',
      'image/vnd.microsoft.icon',
    ];
    if (validTypes.indexOf(file.type) === -1) {
      return false;
    }
    return true;
  };

  const callOnChangeFile = (file) => {
    if (validateTypeFile(file)) {
      onChangeFile(file);
      onError('');
      return;
    }
    onError(t('general_invalid_file_type'));
  };

  const onClickUploadSection = () => {
    fileInputRef?.current?.click();
  };

  const onClickAdjustSize = (e) => {
    e.stopPropagation();
    if (imageBase64) {
      toggleModalCropper();
    }
  };

  const dragOver = (e) => {
    e.preventDefault();
  };

  const dragEnter = (e) => {
    e.preventDefault();
  };

  const dragLeave = (e) => {
    e.preventDefault();
  };

  const fileDrop = (e) => {
    e.preventDefault();
    const { files } = e.dataTransfer;
    callOnChangeFile(files[0]);
  };

  const fileSelected = () => {
    if (fileInputRef.current.files.length) {
      const { files } = fileInputRef.current;
      callOnChangeFile(files[0]);
    }
  };

  return (
    <>
      <div className={classNames(['image-uploader', className])}>
        {required ? (
          <LabelRequiredInput
            color={labelColor}
            label={label}
            className={classNames(['image-uploader__label', labelClassName])}
          />
        ) : (
          <LabelInput
            color={labelColor}
            label={label}
            className={classNames(['image-uploader__label', labelClassName])}
          />
        )}
        <div
          className="image-uploader__upload-section"
          onClick={onClickUploadSection}
          aria-hidden="true"
          onDragOver={dragOver}
          onDragEnter={dragEnter}
          onDragLeave={dragLeave}
          onDrop={fileDrop}
        >
          <UploaderIcon className="upload-section__uploader-icon" />
          <UploadedImage className="upload-section__uploaded_image" imageSrc={imageBase64} />
          <button
            className="upload-section__adjust-size-button"
            type="button"
            onClick={onClickAdjustSize}
          >
            <AdjustSizeIcon className="adjust-size-button__icon" />
            {t('general_adjust_size')}
          </button>
        </div>
        <input ref={fileInputRef} type="file" onChange={fileSelected} />
      </div>
      <ModalImageCropper
        isOpen={openModalCropper}
        toggle={toggleModalCropper}
        imageSrc={imageBase64}
        mainColor={mainColor}
        onSaveData={onSaveBase64}
      />
    </>
  );
};

ImageUploader.defaultProps = {
  className: '',
  labelClassName: '',
  labelColor: undefined,
  required: false,
  imageBase64: '',
  onError: (error) => error,
};

ImageUploader.propTypes = {
  className: PropTypes.string,
  mainColor: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  labelClassName: PropTypes.string,
  labelColor: PropTypes.string,
  required: PropTypes.bool,
  imageBase64: PropTypes.string,
  onChangeFile: PropTypes.func.isRequired,
  onSaveBase64: PropTypes.func.isRequired,
  onError: PropTypes.func,
};
